import { Table, TablePaginationConfig } from 'antd';
import { Patient } from '../../../models';
import { Gender } from '../../../constants/gender';
import { PatientCareLevel } from '../../../constants/careLevel';
import { IndependentLifeLevel } from '../../../constants/independentLifeLevel';
import { DentalIntervention } from '../../../constants/dentalIntervention';
import { OrganizationType } from '../../../constants/orgType';

export type Props = {
  patients: Patient[];
  loading: boolean;
  pagination: TablePaginationConfig;
  onTableChange: (pagination: TablePaginationConfig) => void;
};

export const PatientsTable = (props: Props) => {
  const { patients, loading, pagination, onTableChange } = props;

  const formatScoreWithDate = (
    score: number | null | undefined,
    date: string | null | undefined,
  ) => {
    if (!score && score !== 0) return '-';
    const dateStr = date
      ? new Date(date).toLocaleDateString('ja-JP', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      : '';
    return (
      <div style={{ whiteSpace: 'nowrap' }}>
        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{score}</div>
        {dateStr && (
          <div style={{ fontSize: '11px', color: '#666' }}>{dateStr}</div>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: '施設情報',
      children: [
        {
          title: '施設名',
          dataIndex: ['organization', 'name'],
          key: 'organizationName',
          fixed: 'left' as const,
          width: 150,
        },
        {
          title: '都道府県',
          dataIndex: ['organization', 'prefecture'],
          key: 'organizationPrefecture',
          width: 100,
        },
        {
          title: '施設種別',
          dataIndex: ['organization', 'type'],
          key: 'organizationType',
          width: 150,
          render: (type: string) => OrganizationType.getLabelByKey(type),
        },
      ],
    },
    {
      title: '患者情報',
      children: [
        {
          title: '患者名',
          dataIndex: 'name',
          key: 'name',
          width: 120,
        },
        {
          title: '年齢',
          dataIndex: 'age',
          key: 'age',
          width: 50,
        },
        {
          title: '性別',
          dataIndex: 'gender',
          key: 'gender',
          width: 50,
          render: (gender: string) => Gender.getLabelByKey(gender),
        },
        {
          title: '要介護度',
          dataIndex: 'careLevel',
          key: 'careLevel',
          width: 100,
          render: (level: string) => PatientCareLevel.getLabelByKey(level),
        },
        {
          title: '自立度',
          dataIndex: 'independentLifeLevel',
          key: 'independentLifeLevel',
          width: 60,
          render: (level: string) => IndependentLifeLevel.getShortLabelByKey(level),
        },
        {
          title: '歯科介入',
          dataIndex: 'dentalIntervention',
          key: 'dentalIntervention',
          width: 90,
          render: (intervention: string) =>
            DentalIntervention.getShortLabelByKey(intervention),
        },
      ],
    },
    {
      title: 'OHATスコア',
      children: [
        {
          title: '初回',
          key: 'firstScore',
          width: 100,
          render: (_: any, record: any) =>
            formatScoreWithDate(record.firstScore, record.firstScoreDate),
        },
        {
          title: '2回目',
          key: 'secondScore',
          width: 100,
          render: (_: any, record: any) =>
            formatScoreWithDate(record.secondScore, record.secondScoreDate),
        },
        {
          title: '3回目',
          key: 'thirdScore',
          width: 100,
          render: (_: any, record: any) =>
            formatScoreWithDate(record.thirdScore, record.thirdScoreDate),
        },
        {
          title: '最新',
          key: 'latestScore',
          width: 100,
          render: (_: any, record: any) =>
            formatScoreWithDate(record.latestScore, record.latestScoreDate),
        },
      ],
    },
  ];

  return (
    <Table
      dataSource={patients.map((p) => ({ ...p, key: p.id }))}
      columns={columns}
      loading={loading}
      pagination={pagination}
      onChange={onTableChange}
      size="small"
      bordered
      scroll={{ x: '90%' }}
      style={{ fontSize: 12 }}
    />
  );
};
